/**
 * @module features/clients/components/ClientQuotaGauge
 * @description Visual capacity bar displaying active user count vs max limit with
 * emerald / amber / rose threshold color-coding and accessible ARIA attributes.
 */
import * as React from "react";
import { Users } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ClientQuotaGaugeProps {
  activeCount: number;
  maxLimit: number;
  compact?: boolean;
  showIcon?: boolean;
  className?: string;
}

export function ClientQuotaGauge({
  activeCount,
  maxLimit,
  compact = false,
  showIcon = false,
  className,
}: ClientQuotaGaugeProps) {
  const percent = maxLimit > 0 ? Math.min(Math.round((activeCount / maxLimit) * 100), 100) : 0;

  // Threshold color coding
  let barColor = "bg-emerald-500";
  let textColor = "text-emerald-700 dark:text-emerald-400";
  let badgeBg = "bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800/60";

  if (percent >= 100) {
    barColor = "bg-rose-500";
    textColor = "text-rose-700 dark:text-rose-400";
    badgeBg = "bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-800/60";
  } else if (percent >= 80) {
    barColor = "bg-amber-500";
    textColor = "text-amber-700 dark:text-amber-400";
    badgeBg = "bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800/60";
  }

  if (compact) {
    return (
      <div className={cn("flex flex-col gap-1 w-full max-w-[140px]", className)}>
        <div className="flex items-center justify-between text-xs font-medium">
          <span className="text-muted-foreground flex items-center gap-1 font-mono text-[11px]">
            {showIcon && <Users className="h-3 w-3 inline text-muted-foreground/70" />}
            {activeCount}/{maxLimit}
          </span>
          <span className={cn("font-mono text-[11px] font-semibold", textColor)}>{percent}%</span>
        </div>
        <div
          role="progressbar"
          aria-valuenow={activeCount}
          aria-valuemin={0}
          aria-valuemax={maxLimit}
          aria-label={`Quota utilization ${percent}% (${activeCount} of ${maxLimit} users)`}
          className="h-1.5 w-full bg-muted rounded-full overflow-hidden"
        >
          <div
            className={cn("h-full transition-all duration-300 rounded-full", barColor)}
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={cn("p-3 sm:p-3.5 rounded-xl border border-border/80 dark:border-zinc-800 bg-card/60 space-y-2.5", className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-900 dark:bg-zinc-800 text-white dark:text-zinc-100 ring-1 ring-black/5 dark:ring-white/10 shrink-0">
            <Users className="h-4 w-4" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground font-medium">Seat Capacity</p>
            <p className="text-sm font-semibold text-foreground font-mono">
              {activeCount} <span className="text-muted-foreground font-normal">/ {maxLimit} seats</span>
            </p>
          </div>
        </div>
        <div className={cn("text-[11px] font-mono font-semibold px-2 py-0.5 rounded-full border", badgeBg, textColor)}>
          {percent}% Used
        </div>
      </div>

      <div
        role="progressbar"
        aria-valuenow={activeCount}
        aria-valuemin={0}
        aria-valuemax={maxLimit}
        aria-label={`User capacity utilization: ${activeCount} of ${maxLimit} used (${percent}%)`}
        className="h-2 w-full bg-muted rounded-full overflow-hidden"
      >
        <div
          className={cn("h-full transition-all duration-300 rounded-full", barColor)}
          style={{ width: `${percent}%` }}
        />
      </div>

      <div className="flex justify-between items-center text-[11px] text-muted-foreground font-mono">
        <span>{Math.max(0, maxLimit - activeCount)} seats available</span>
        {percent >= 100 && (
          <span className="text-rose-600 dark:text-rose-400 font-semibold">Capacity reached</span>
        )}
      </div>
    </div>
  );
}
